import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, MailWarning, Layers, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  New: "text-brand-emerald border-brand-emerald/40 bg-brand-emerald/10",
  Contacted: "text-brand-azure border-brand-azure/40 bg-brand-azure/10",
  Qualified: "text-yellow-300 border-yellow-300/30 bg-yellow-300/10",
  Converted: "text-brand-snow border-brand-snow/30 bg-brand-snow/10",
  Closed: "text-brand-mist border-brand-mist/30 bg-brand-mist/10",
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_COLORS[status] || STATUS_COLORS.New}`} data-testid={`status-badge-${status}`}>
    {status}
  </span>
);

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setError(true));
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  if (error) {
    if (me?.role === "member") {
      return (
        <div className="space-y-3" data-testid="stats-error">
          <p className="text-sm text-brand-mist">Welcome to your project workspace. Open Projects to see your assigned projects, tasks and files.</p>
          <Link to="/admin/projects" data-testid="overview-goto-projects" className="inline-flex items-center gap-1.5 rounded-full bg-brand-emerald px-4 py-2 font-display text-sm font-semibold text-brand-black">
            Go to Projects <ArrowRight size={14} />
          </Link>
        </div>
      );
    }
    if (me?.role === "sales") {
      return (
        <div className="space-y-3" data-testid="stats-error">
          <p className="text-sm text-brand-mist">Welcome to your sales workspace. Open Sales Leads to manage your pipeline.</p>
          <Link to="/admin/leads" data-testid="overview-goto-leads" className="inline-flex items-center gap-1.5 rounded-full bg-brand-emerald px-4 py-2 font-display text-sm font-semibold text-brand-black">
            Go to Sales Leads <ArrowRight size={14} />
          </Link>
        </div>
      );
    }
    return <p className="text-sm text-brand-mist" data-testid="stats-error">Overview statistics are available to admin roles. Head to Articles, Content or Portfolio to manage site content.</p>;
  }
  if (!stats) return <p className="text-sm text-brand-mist">Loading…</p>;

  const cards = [
    { label: "Total enquiries", value: stats.total_enquiries, testid: "stat-total" },
    { label: "New", value: stats.by_status.New, testid: "stat-new" },
    { label: "Contacted", value: stats.by_status.Contacted, testid: "stat-contacted" },
    { label: "Qualified", value: stats.by_status.Qualified, testid: "stat-qualified" },
    { label: "Converted", value: stats.by_status.Converted, testid: "stat-converted" },
    { label: "Closed", value: stats.by_status.Closed, testid: "stat-closed" },
  ];

  return (
    <div className="space-y-10" data-testid="admin-overview">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Overview</h1>
        <p className="text-sm text-brand-mist">Live numbers from the enquiry pipeline and catalogue.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6" data-testid="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="surface-card space-y-2 p-5" data-testid={c.testid}>
            <p className="text-xs text-brand-mist">{c.label}</p>
            <p className="font-display text-3xl font-bold text-brand-snow">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="surface-card flex items-center justify-between p-5" data-testid="stat-failed-emails">
          <div className="flex items-center gap-4">
            <span className="rounded-lg border border-red-400/30 bg-red-400/10 p-2.5 text-red-300"><MailWarning size={18} /></span>
            <div>
              <p className="text-xs text-brand-mist">Failed email notifications</p>
              <p className="font-display text-2xl font-bold text-brand-snow">{stats.failed_emails}</p>
            </div>
          </div>
          <Link to="/admin/enquiries" className="text-xs font-medium text-brand-emerald hover:underline" data-testid="stat-failed-link">Review</Link>
        </div>
        <div className="surface-card flex items-center justify-between p-5" data-testid="stat-services">
          <div className="flex items-center gap-4">
            <span className="rounded-lg border border-brand-azure/30 bg-brand-azure/10 p-2.5 text-brand-azure"><Layers size={18} /></span>
            <div>
              <p className="text-xs text-brand-mist">Published services</p>
              <p className="font-display text-2xl font-bold text-brand-snow">{stats.services_published}</p>
            </div>
          </div>
          <Link to="/admin/services" className="text-xs font-medium text-brand-emerald hover:underline" data-testid="stat-services-link">Manage</Link>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-brand-snow">Recent enquiries</h2>
          <Link to="/admin/enquiries" data-testid="recent-view-all" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-emerald">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="surface-card divide-y divide-brand-mist/10" data-testid="recent-enquiries">
          {stats.recent.length === 0 && <p className="p-6 text-sm text-brand-mist">No enquiries yet.</p>}
          {stats.recent.map((e) => (
            <Link key={e.enquiry_id} to={`/admin/enquiries?open=${e.enquiry_id}`} data-testid={`recent-${e.enquiry_id}`} className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-white/5">
              <div className="flex items-center gap-4">
                <span className="rounded-lg border border-brand-mist/15 bg-brand-black p-2 text-brand-emerald"><Inbox size={15} /></span>
                <div>
                  <p className="text-sm font-medium text-brand-snow">{e.full_name}</p>
                  <p className="text-xs text-brand-mist">{e.service_name || "General"} · {e.country}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="hidden text-xs text-brand-mist sm:inline">{new Date(e.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                <StatusBadge status={e.status} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
